/**
 * CUSTOMIZATION GUIDE
 * ===================
 *
 * This file contains all the content and data for your link hub.
 * You can easily customize everything below!
 *
 * 📝 CONTENT CUSTOMIZATION (in this file):
 * ----------------------------------------
 * 1. Profile Information (line ~90):
 *    - name: Your display name
 *    - bio: Your tagline/description
 *    - avatar: banner.png
 *
 * 2. Social Links (line ~96):
 *    - Add/remove social platforms
 *    - Update URLs to your profiles
 *
 * 3. Main Links (line ~105):
 *    - Add/remove link cards
 *    - Customize titles, descriptions, URLs
 *    - Change gradient colors for each card
 *
 * 4. Testimonials (line ~140):
 *    - Add your client testimonials
 *    - Update names, roles, companies, avatars
 *    - Adjust ratings (1-5 stars)
 *
 * 5. Favorite Quotes (line ~167):
 *    - Add your inspirational quotes
 *    - Update quote text and authors
 *
 * 6. Free Course (line ~183):
 *    - Update course title, description
 *    - Set PDF and video URLs
 *    - Change thumbnail image
 *    - Update duration and lesson count
 *
 * 7. About Me Section (line ~195):
 *    - Update your photo
 *    - Write your story/description (supports multiple paragraphs with \n\n)
 *    - Add your highlights/achievements
 *    - Customize title
 *
 * 8. QR Code Section (line ~310):
 *    - Update QR code image (can be actual QR code or placeholder)
 *    - Add your photo
 *    - Customize title and description
 *    - Badge text can be modified in the JSX
 *
 * 🎨 FONTS CUSTOMIZATION:
 * ----------------------
 * Current fonts: Inter Variable (body) + Playfair Display (headings)
 *
 * To change fonts:
 * 1. Install new font: Run in terminal:
 *    npm install @fontsource-variable/[font-name]
 *    (or @fontsource/[font-name] for non-variable fonts)
 *
 * 2. Import in src/main.tsx (add below existing imports):
 *    import '@fontsource-variable/[font-name]'
 *
 * 3. Update tailwind.config.ts:
 *    In fontFamily section, change:
 *    sans: ['Your Font Variable', 'Your Font', 'system-ui', 'sans-serif']
 *    display: ['Your Heading Font', 'serif']
 *
 * Popular font options:
 * - Modern: outfit, manrope, dm-sans
 * - Professional: roboto, open-sans, source-sans-pro
 * - Creative: poppins, nunito, comfortaa
 *
 * 🎨 COLOR CUSTOMIZATION:
 * ----------------------
 * Edit colors in: src/index.css
 *
 * Main colors to customize:
 * - --primary: Main brand color (currently purple)
 * - --accent: Secondary accent color
 * - --background: Page background
 * - --foreground: Text color
 *
 * Current color scheme:
 * - Primary: Purple (262 83% 58%)
 * - Accent: Purple variant (262 83% 58%)
 * - Background: Light gray (240 20% 99%)
 *
 * To change colors:
 * 1. Open src/index.css
 * 2. Find :root { } section (for light mode)
 * 3. Find .dark { } section (for dark mode)
 * 4. Update HSL values (Hue Saturation Lightness)
 *
 * Color picker tip: Use https://uicolors.app/create
 *
 * 🔧 OTHER CUSTOMIZATIONS:
 * -----------------------
 * - Banner height: Change h-48 md:h-64 in banner section (line ~208)
 * - Card spacing: Modify mb-16 values throughout
 * - Animation speed: Update duration-XXX classes
 * - Border radius: Change rounded-XXX classes
 */

import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel';
import {
  Zap,
  Twitter,
  Mail,
  Video,
  Youtube,
  ArrowUpRight,
  Quote,
  Star,
  FileText,
  Download,
  Play,
  GraduationCap
} from 'lucide-react';
import Autoplay from 'embla-carousel-autoplay';

// Link data structure
interface Link {
  title: string;
  description: string;
  url: string;
  icon?: React.ReactNode;
  gradient?: string;
}

// Testimonial data structure
interface Testimonial {
  name: string;
  role: string;
  company: string;
  content: string;
  avatar: string;
  rating: number;
}

// Quote data structure
interface QuoteItem {
  text: string;
  author: string;
}

// Course data structure
interface Course {
  title: string;
  description: string;
  thumbnail: string;
  pdfUrl: string;
  videoUrl: string;
  duration: string;
  lessons: number;
}

// About Me data structure
interface AboutMe {
  image: string;
  title: string;
  description: string;
  highlights: string[];
}

// QR Section data structure
interface QRSection {
  title: string;
  description: string;
  qrCode: string;
  photo: string;
}

const Index = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [api, setApi] = useState<CarouselApi>();
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [scrollY, setScrollY] = useState(0);
  const [highlightedSocial, setHighlightedSocial] = useState<number | null>(null);
  const [socialAnimationComplete, setSocialAnimationComplete] = useState(false);
  
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Trigger load animation
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Sequential social icon highlight animation
  useEffect(() => {
    if (!isLoaded) return;

    const animateSocials = async () => {
      // Wait for initial page load animation
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Highlight each social icon for 3 seconds
      for (let i = 0; i < socials.length; i++) {
        setHighlightedSocial(i);
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      // Reset to normal state
      setHighlightedSocial(null);
      setSocialAnimationComplete(true);
    };

    animateSocials();
  }, [isLoaded]);

  // Auto-rotate testimonials every 5 seconds
  useEffect(() => {
    if (!api) return;

    const interval = setInterval(() => {
      api.scrollNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [api]);

  // Track scroll position for parallax gradient
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Enhanced scroll reveal animation with Intersection Observer
  useEffect(() => {
    // Wait for initial load animation to complete
    const timer = setTimeout(() => {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      };

      const observerCallback = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.getAttribute('data-section');
            if (sectionId) {
              setVisibleSections((prev) => new Set(prev).add(sectionId));
            }
          }
        });
      };

      const observer = new IntersectionObserver(observerCallback, observerOptions);

      // Observe all sections
      Object.values(sectionRefs.current).forEach((ref) => {
        if (ref) observer.observe(ref);
      });

      return () => observer.disconnect();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Profile information
  const profile = {
    name: "",
    bio: "",
    avatar: "/banner.png",
  };

  // Social media links
  const socials = [
    { icon: <Twitter className="w-5 h-5" />, url: "https://x.com/88_sats", label: "Twitter", displayLabel: "Twitter" },
    { icon: <Zap className="w-5 h-5" />, url: "https://primal.net/p/nprofile1qqs8cajagp7n48275ytuhzuxn93g2crc0lqgfgx8dta2gjdlh5fpmpq2t829j", label: "Nostr", displayLabel: "Nostr" },
    { icon: <Video className="w-5 h-5" />, url: "https://rumble.com/user/88sats", label: "Rumble", displayLabel: "Rumble" },
    { icon: <Youtube className="w-5 h-5" />, url: "https://www.youtube.com/@88SATSradio", label: "YouTube", displayLabel: "YouTube" },
    { icon: <Mail className="w-5 h-5" />, url: "mailto:88sats@proton.me", label: "Email", displayLabel: "Email" },
  ];

  // Main links
  const links: Link[] = [
    {
      title: "Bitcoin Skool Course + Community",
      description: "Access to Bitcoin Skool daily videos, weekly Skool live session, and full access to Skool community with all exclusive content",
      url: "https://www.skool.com/88sats",
      gradient: "from-orange-400 to-orange-500",
    },
    {
      title: "Bitcoin Skool Course",
      description: "Access to Bitcoin Skool daily videos and other exclusive Bitcoin content",
      url: "https://www.patreon.com/c/u83780281",
      gradient: "from-orange-400 to-orange-500",
    },
    {
      title: "Bitcoin Daily Live Streams",
      description: "Live on Rumble and Nostr every morning at 10am EST",
      url: "https://rumble.com/user/88sats",
      gradient: "from-orange-400 to-orange-500",
    },
    {
      title: "Book a call with Jor",
      description: "Need help with Bitcoin security or strategy? Book a 1-on-1 call with Jor",
      url: "https://calendly.com/bitcoinjourney/",
      gradient: "from-orange-400 to-orange-500",
    },
    {
      title: "Maple Bitcoin",
      description: "A collaboration with Rajat and Forrest to spread Bitcoin adoption across the globe",
      url: "https://www.youtube.com/@MapleBitcoin",
      gradient: "from-orange-400 to-orange-500",
    },
    {
      title: "Shop & Merch",
      description: "Bitcoin-themed merchandise and products to show your support",
      url: "#",
      gradient: "from-orange-400 to-orange-500",
    },
  ];

  // Testimonials
  const testimonials: Testimonial[] = [
    {
      name: "Arnie",
      role: "CEO",
      company: "Woofers Inc.",
      content: "Jor is the King of Everything",
      avatar: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=400",
      rating: 5,
    },
    {
      name: "Michael Chen",
      role: "Product Manager",
      company: "InnovateCo",
      content: "Exceptional work! The project was delivered on time and the quality was outstanding. A true professional who understands both design and development.",
      avatar: "https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=400",
      rating: 5,
    },
    {
      name: "Emily Rodriguez",
      role: "Creative Director",
      company: "Design Studio",
      content: "Amazing collaboration! The creativity and technical expertise brought our vision to life in ways we didn't think possible. Can't wait to work together again!",
      avatar: "https://images.pexels.com/photos/1130626/pexels-photo-1130626.jpeg?auto=compress&cs=tinysrgb&w=400",
      rating: 5,
    },
    {
      name: "David Kim",
      role: "Founder",
      company: "StartupHub",
      content: "Incredible talent and professionalism. Every interaction was smooth and the final results were beyond impressive. A game-changer for our business!",
      avatar: "https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=400",
      rating: 5,
    },
  ];

  // Favorite quotes
  const quotes: QuoteItem[] = [
    {
      text: "The only way to do great work is to love what you do.",
      author: "Steve Jobs",
    },
    {
      text: "Design is not just what it looks like and feels like. Design is how it works.",
      author: "Steve Jobs",
    },
    {
      text: "Simplicity is the ultimate sophistication.",
      author: "Leonardo da Vinci",
    },
  ];

  // Free course
  const course: Course = {
    title: "21 Day Bitcoin Journey - Genesis Edition",
    description: "Learn how to buy Bitcoin, send Bitcoin, best practices for securing your Bitcoin, and much more in less than two hours.",
    thumbnail: "https://i.nostr.build/xo2ncP6BaBr36qaf.png",
    pdfUrl: "#",
    videoUrl: "https://www.patreon.com/collection/1900586",
    duration: "2 hours",
    lessons: 11,
  };

  // About Me
  const aboutMe: AboutMe = {
    image: "https://i.nostr.build/1Q9I80N8KZHWbcy8.jpg",
    title: "A little about Jor",
    description: "I'm passionate about creating meaningful experiences through design and technology. With over a decade of experience, I've helped countless individuals and businesses bring their visions to life.\n\nMy journey began with a simple curiosity about how things work, which evolved into a deep love for crafting digital solutions that make a real difference. I believe in the power of creativity, continuous learning, and authentic connections.",
    highlights: [
      "10+ years of industry experience",
      "Worked with 100+ clients worldwide",
      "Speaker at international conferences",
      "Published author and educator",
    ],
  };

  // QR Code Section
  const qrSection: QRSection = {
    title: "Rescue Dog Fund",
    description: "Scan the QR code to save my contact information or share this page with others. You can also download my digital business card for easy access to all my links.",
    qrCode: "https://i.nostr.build/ZYDc4YdC7lENBbM6.jpg",
    photo: "https://i.nostr.build/SFUDpvRxF51qNN1G.jpg",
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-orange-50/60 via-amber-50/40 to-orange-50/60">
      {/* Rich animated gradient background with scroll parallax */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Scroll-following gradient layer */}
        <div 
          className="absolute inset-0 transition-transform duration-700 ease-out"
          style={{
            transform: `translateY(${scrollY * 0.15}px)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-orange-100/40 via-amber-100/30 to-orange-100/40" />
        </div>

        {/* Moving gradient orbs - enhanced with parallax */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div 
            className="absolute top-[10%] left-[10%] w-[700px] h-[700px] bg-gradient-to-br from-orange-500/45 via-amber-500/35 to-orange-400/45 rounded-full mix-blend-multiply filter blur-3xl animate-float opacity-75"
            style={{
              transform: `translateY(${scrollY * 0.1}px)`,
            }}
          />
          <div 
            className="absolute top-[60%] right-[15%] w-[600px] h-[600px] bg-gradient-to-br from-amber-500/45 via-orange-400/35 to-yellow-500/45 rounded-full mix-blend-multiply filter blur-3xl animate-float-delayed opacity-75"
            style={{
              transform: `translateY(${scrollY * 0.08}px)`,
            }}
          />
          <div 
            className="absolute bottom-[10%] left-[40%] w-[650px] h-[650px] bg-gradient-to-br from-orange-400/45 via-amber-400/35 to-orange-500/45 rounded-full mix-blend-multiply filter blur-3xl animate-float-slower opacity-75"
            style={{
              transform: `translateY(${scrollY * 0.12}px)`,
            }}
          />
        </div>
        
        {/* Additional depth layers with parallax */}
        <div 
          className="absolute inset-0 bg-gradient-to-br from-orange-100/35 via-transparent to-amber-100/35 animate-gradient-shift"
          style={{
            transform: `translateY(${scrollY * 0.05}px)`,
          }}
        />
        
        {/* Radial gradient for vignette effect */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,transparent_0%,rgba(251,146,60,0.06)_100%)]" />
        
        {/* Subtle noise texture */}
        <div className="absolute inset-0 noise-texture" />
        
        {/* Glassmorphic overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-white/5" />
      </div>

      {/* Main content */}
      <div className="relative z-10 container max-w-2xl mx-auto px-4 py-12 md:py-20">
        {/* Profile section with banner */}
        <div className="mb-12">
          {/* Banner Image */}
          <div className={`relative w-full h-48 md:h-64 rounded-3xl overflow-hidden mb-8 group bg-black shadow-2xl shadow-orange-500/30 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}>
            {/* Glow ring */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-700" />
            
            <div className="relative w-full h-full rounded-3xl overflow-hidden">
              <img
                src={profile.avatar}
                alt="Profile banner"
                className="w-full h-full object-cover object-center transform group-hover:scale-110 transition-transform duration-1000 ease-out"
              />
              {/* Rich overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/5 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-amber-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            </div>
          </div>

          {/* Profile Info */}
          <div className="text-center -mt-16 relative z-30">
            {profile.name && (
              <h1 className={`text-4xl md:text-5xl font-display font-bold mb-3 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 bg-clip-text text-transparent animate-gradient text-shadow transition-all duration-700 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                {profile.name}
              </h1>
            )}

            {profile.bio && (
              <p className={`text-lg md:text-xl text-foreground mb-8 flex items-center justify-center gap-2 text-shadow transition-all duration-700 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                {profile.bio}
              </p>
            )}

            {/* Social links */}
            <div className={`flex items-center justify-center gap-6 mb-8 transition-all duration-700 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {socials.map((social, index) => {
                const isHighlighted = highlightedSocial === index;
                const shouldShowLabel = isHighlighted && !socialAnimationComplete;
                
                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="group relative flex flex-col items-center"
                    style={{
                      transitionDelay: `${400 + index * 50}ms`,
                    }}
                  >
                    <div className="relative">
                      {/* Outer glow - enhanced for highlight animation */}
                      <div className={`absolute inset-0 rounded-full bg-gradient-to-br from-orange-400 via-amber-500 to-orange-600 blur-xl transition-all duration-700 ${
                        isHighlighted ? 'opacity-100 scale-125 animate-social-glow-pulse' : 'opacity-0 group-hover:opacity-100 group-hover:scale-100'
                      }`} />
                      
                      {/* Middle ring - enhanced for highlight animation */}
                      <div className={`absolute -inset-0.5 rounded-full bg-gradient-to-br from-orange-500/30 to-amber-500/30 blur-md transition-all duration-700 ${
                        isHighlighted ? 'opacity-100 scale-110' : 'opacity-0 group-hover:opacity-100'
                      }`} />
                      
                      {/* Icon button - enhanced for highlight animation */}
                      <div className={`relative w-14 h-14 rounded-full glass-effect border-2 shadow-xl flex items-center justify-center transition-all duration-700 ${
                        isHighlighted 
                          ? 'text-white bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 border-orange-400 scale-110 shadow-2xl shadow-orange-500/50 animate-social-pulse'
                          : 'text-gray-700 border-orange-200/70 shadow-orange-500/15 group-hover:text-white group-hover:bg-gradient-to-br group-hover:from-orange-500 group-hover:via-orange-600 group-hover:to-amber-600 group-hover:border-orange-400 group-hover:scale-110 group-hover:shadow-2xl group-hover:shadow-orange-500/40'
                      }`}>
                        {social.icon}
                      </div>
                    </div>

                    {/* Label text - appears during highlight animation */}
                    <div className={`absolute -bottom-7 whitespace-nowrap transition-all duration-500 ${
                      shouldShowLabel 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 translate-y-2 pointer-events-none'
                    }`}>
                      <span className="text-xs font-bold text-orange-600 tracking-wider px-3 py-1 rounded-full bg-white/90 backdrop-blur-sm shadow-lg shadow-orange-500/25 border border-orange-200/60">
                        {social.displayLabel}
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Links grid */}
        <div 
          ref={(el) => (sectionRefs.current['links'] = el)}
          data-section="links"
          className="space-y-5 mb-16"
        >
          {links.map((link, index) => (
            <Card
              key={index}
              className={`group relative overflow-hidden transition-all duration-700 hover:shadow-2xl hover:shadow-orange-500/30 border-2 border-orange-200/60 hover:border-orange-400/80 cursor-pointer glass-frosted hover:-translate-y-2 reveal-element ${visibleSections.has('links') ? 'is-visible' : ''} ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{
                transitionDelay: visibleSections.has('links') ? `${index * 120}ms` : `${500 + index * 80}ms`,
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => window.open(link.url, '_blank')}
            >
              {/* Glassmorphic inner border */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                <div className="absolute inset-0 rounded-[inherit] border border-white/40" />
              </div>

              {/* Rich gradient glow background */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 via-amber-500/0 to-orange-500/0 group-hover:from-orange-500/10 group-hover:via-amber-500/10 group-hover:to-orange-500/10 transition-all duration-700" />

              {/* Enhanced shimmer effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1400 bg-gradient-to-r from-transparent via-white/80 to-transparent" />

              {/* Frosted glass inner glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-orange-300/60 to-transparent blur-sm" />
                <div className="absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-orange-300/60 to-transparent blur-sm" />
                <div className="absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-transparent via-orange-300/40 to-transparent blur-sm" />
                <div className="absolute inset-y-0 right-0 w-[2px] bg-gradient-to-b from-transparent via-orange-300/40 to-transparent blur-sm" />
              </div>

              <div className="relative p-7 md:p-8 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl md:text-2xl font-bold mb-2 group-hover:text-orange-600 transition-colors duration-400 tracking-tight text-shadow-lg">
                    {link.title}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground/90 group-hover:text-foreground transition-colors duration-400 leading-relaxed">
                    {link.description}
                  </p>
                </div>

                {/* Beautiful enhanced arrow button */}
                <div className="flex-shrink-0">
                  <div className="relative w-16 h-16 md:w-18 md:h-18">
                    {/* Outer glow ring */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-orange-400 via-amber-500 to-orange-600 opacity-0 group-hover:opacity-30 blur-2xl transition-all duration-500" />
                    
                    {/* Middle ring */}
                    <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-orange-500/20 to-amber-500/20 opacity-0 group-hover:opacity-100 blur-md transition-all duration-500" />
                    
                    {/* Button */}
                    <div className="relative w-full h-full rounded-full bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 shadow-lg shadow-orange-500/40 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-orange-500/60">
                      <ArrowUpRight className="w-6 h-6 md:w-7 md:h-7 text-white transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced bottom accent line with glow */}
              <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-center shadow-lg shadow-orange-500/50" />
            </Card>
          ))}
        </div>

        {/* Free Course Section */}
        <div 
          ref={(el) => (sectionRefs.current['course'] = el)}
          data-section="course"
          className={`mb-16 reveal-fade ${visibleSections.has('course') ? 'is-visible' : ''}`}
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center gap-2 mb-3">
              <GraduationCap className="w-8 h-8 text-orange-500" />
              <h2 className="text-3xl md:text-4xl font-display font-bold bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 bg-clip-text text-transparent animate-gradient">
                Free Course
              </h2>
            </div>
            <p className="text-muted-foreground">
              My gift to you — Start learning today
            </p>
          </div>

          <Card className="overflow-hidden border-2 border-orange-200/60 hover:border-orange-400/80 transition-all duration-500 hover:shadow-2xl hover:shadow-orange-500/30 glass-card">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Course Image */}
              <div className="relative h-64 md:h-auto overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/25 via-amber-500/15 to-orange-500/25 z-10" />
                <img
                  src={course.thumbnail}
                  alt={course.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-1000"
                />
                {/* Sexy glowing FREE badge */}
                <div className="absolute top-6 right-6 z-20">
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-amber-500 rounded-full blur-xl opacity-60 animate-pulse" />
                  <div className="relative bg-gradient-to-r from-orange-500 via-orange-600 to-amber-600 text-white px-5 py-2 rounded-full text-sm font-extrabold shadow-2xl shadow-orange-500/60 tracking-wider">
                    FREE
                  </div>
                </div>
              </div>

              {/* Course Info */}
              <div className="p-8">
                <h3 className="text-2xl md:text-3xl font-display font-bold mb-3">
                  {course.title}
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {course.description}
                </p>

                {/* Course Stats */}
                <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-border">
                  <div className="flex items-center gap-2 text-sm">
                    <Video className="w-4 h-4 text-orange-500" />
                    <span className="text-muted-foreground font-medium">{course.duration}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-orange-500" />
                    <span className="text-muted-foreground font-medium">{course.lessons} lessons</span>
                  </div>
                </div>

                {/* Download Buttons */}
                <div className="space-y-3">
                  <Button
                    className="w-full group relative overflow-hidden bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 hover:shadow-xl hover:shadow-orange-500/50 transition-all duration-300 text-white font-bold text-base h-12"
                    onClick={() => window.open(course.videoUrl, '_blank')}
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                      Watch Video Course
                    </span>
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full group border-2 border-orange-200 hover:border-orange-400 hover:bg-orange-50 transition-all duration-300 font-bold text-base text-foreground hover:text-orange-600 h-12"
                    onClick={() => window.open(course.pdfUrl, '_blank')}
                  >
                    <Download className="w-5 h-5 mr-2 group-hover:translate-y-0.5 transition-transform" />
                    Download PDF Guide
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Testimonials Section */}
        <div 
          ref={(el) => (sectionRefs.current['testimonials'] = el)}
          data-section="testimonials"
          className={`mb-16 reveal-fade ${visibleSections.has('testimonials') ? 'is-visible' : ''}`}
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3 bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
              What People Say
            </h2>
            <p className="text-muted-foreground">
              Don't just take my word for it
            </p>
          </div>

          <Carousel
            setApi={setApi}
            opts={{
              align: "start",
              loop: true,
            }}
            plugins={[
              Autoplay({
                delay: 5000,
                stopOnInteraction: true,
                stopOnMouseEnter: true,
              }),
            ]}
            className="w-full"
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-full">
                  <Card className="border-2 border-orange-200/60 hover:border-orange-400/80 transition-all duration-500 hover:shadow-2xl hover:shadow-orange-500/30 glass-card hover:-translate-y-1">
                    <div className="p-8 relative">
                      {/* Glassmorphic corner accents */}
                      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/8 via-orange-400/4 to-transparent rounded-bl-3xl backdrop-blur-sm" />
                      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-amber-500/8 via-amber-400/4 to-transparent rounded-tr-3xl backdrop-blur-sm" />
                      
                      {/* Inner glass border */}
                      <div className="absolute inset-0 rounded-[inherit] border border-white/30 pointer-events-none" />
                      {/* Stars */}
                      <div className="flex gap-1 mb-5 relative z-10">
                        {Array.from({ length: testimonial.rating }).map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-500 drop-shadow-lg" />
                        ))}
                      </div>

                      {/* Quote */}
                      <div className="relative mb-6 z-10">
                        <Quote className="absolute -top-2 -left-2 w-12 h-12 text-orange-500/15" />
                        <p className="text-foreground pl-10 italic leading-relaxed text-base md:text-lg">
                          "{testimonial.content}"
                        </p>
                      </div>

                      {/* Author */}
                      <div className="flex items-center gap-4 pt-6 border-t border-orange-200/60 relative z-10">
                        <div className="relative">
                          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 opacity-30 blur-md" />
                          <Avatar className="relative w-14 h-14 ring-2 ring-orange-400/50 ring-offset-2 ring-offset-white">
                            <AvatarImage src={testimonial.avatar} alt={testimonial.name} />
                            <AvatarFallback className="bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 text-white font-bold">{testimonial.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                        </div>
                        <div>
                          <p className="font-bold text-foreground text-base">{testimonial.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {testimonial.role} at {testimonial.company}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-12 border-orange-200 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-600 transition-all duration-300" />
            <CarouselNext className="hidden md:flex -right-12 border-orange-200 hover:bg-orange-50 hover:border-orange-400 hover:text-orange-600 transition-all duration-300" />
          </Carousel>
        </div>

        {/* About Me Section */}
        <div 
          ref={(el) => (sectionRefs.current['about'] = el)}
          data-section="about"
          className={`mb-16 reveal-scale ${visibleSections.has('about') ? 'is-visible' : ''}`}
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3 bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
              {aboutMe.title}
            </h2>
            <p className="text-muted-foreground">
              Get to know the person behind the work
            </p>
          </div>

          <Card className="overflow-hidden border-2 border-orange-200/60 hover:border-orange-400/80 transition-all duration-500 hover:shadow-2xl hover:shadow-orange-500/30 glass-card">
            {/* Inner glass border */}
            <div className="absolute inset-0 rounded-[inherit] border border-white/30 pointer-events-none z-10" />
            
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image Side */}
              <div className="relative h-96 md:h-auto overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/12 via-transparent to-amber-500/12 z-10" />
                <img
                  src={aboutMe.image}
                  alt="About me"
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-1000"
                />
                {/* Glassmorphic decorative bar */}
                <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 shadow-lg shadow-orange-500/50 backdrop-blur-sm" />
              </div>

              {/* Content Side */}
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <div className="space-y-6">
                  {/* Description */}
                  <div className="space-y-4">
                    {aboutMe.description.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="text-foreground leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  {/* Highlights */}
                  <div className="pt-6 border-t border-border">
                    <h3 className="text-sm font-semibold text-orange-600 uppercase tracking-wider mb-4">
                      Highlights
                    </h3>
                    <ul className="space-y-3">
                      {aboutMe.highlights.map((highlight, index) => (
                        <li key={index} className="flex items-start gap-3 group/item">
                          <div className="mt-1">
                            <div className="w-2 h-2 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 group-hover/item:scale-150 transition-transform duration-300 shadow-sm" />
                          </div>
                          <span className="text-muted-foreground group-hover/item:text-foreground transition-colors duration-300">
                            {highlight}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Favorite Quotes Section */}
        <div 
          ref={(el) => (sectionRefs.current['quotes'] = el)}
          data-section="quotes"
          className={`mb-16 reveal-fade ${visibleSections.has('quotes') ? 'is-visible' : ''}`}
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3 bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
              Words That Inspire
            </h2>
            <p className="text-muted-foreground">
              Quotes that guide my journey
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {quotes.map((quote, index) => (
              <Card
                key={index}
                className="group relative overflow-hidden border-2 border-orange-200/60 hover:border-amber-400/80 transition-all duration-500 hover:shadow-xl hover:shadow-amber-500/25 glass-card"
              >
                {/* Glassmorphic glow */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-amber-500/15 via-amber-400/8 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 backdrop-blur-sm" />

                {/* Inner glass border */}
                <div className="absolute inset-0 rounded-[inherit] border border-white/30 pointer-events-none" />

                <div className="relative p-8">
                  <Quote className="w-12 h-12 text-amber-500/25 mb-4" />
                  <p className="text-lg font-medium text-foreground mb-6 leading-relaxed">
                    {quote.text}
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="h-px flex-1 bg-gradient-to-r from-amber-500/50 to-transparent" />
                    <p className="text-sm font-bold text-amber-600 tracking-wide">
                      {quote.author}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* QR Code & Contact Section */}
        <div 
          ref={(el) => (sectionRefs.current['qr'] = el)}
          data-section="qr"
          className={`mb-16 reveal-fade ${visibleSections.has('qr') ? 'is-visible' : ''}`}
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3 bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
              {qrSection.title}
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {qrSection.description}
            </p>
          </div>

          <Card className="overflow-hidden border-2 border-orange-200/60 hover:border-orange-400/80 transition-all duration-500 hover:shadow-2xl hover:shadow-orange-500/30 glass-card">
            {/* Inner glass border */}
            <div className="absolute inset-0 rounded-[inherit] border border-white/30 pointer-events-none z-10" />
            
            <div className="grid md:grid-cols-3 gap-6 p-8">
              {/* QR Code */}
              <div className="flex flex-col items-center justify-center group">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500/30 to-amber-500/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative bg-white p-5 rounded-2xl shadow-xl group-hover:scale-105 transition-transform duration-300 border-2 border-orange-200/50 group-hover:border-orange-400/80">
                    <img
                      src={qrSection.qrCode}
                      alt="QR Code"
                      className="w-40 h-40 md:w-48 md:h-48"
                    />
                  </div>
                </div>
                <p className="mt-5 text-sm font-bold text-orange-600 tracking-wide">
                  SCAN TO DONATE
                </p>
              </div>

              {/* Description (spans 2 columns on desktop) */}
              <div className="md:col-span-2 flex flex-col justify-center space-y-6">
                {/* Photo */}
                <div className="relative h-48 md:h-64 rounded-xl overflow-hidden group/photo border-2 border-orange-200/50">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-amber-500/10 z-10" />
                  <img
                    src={qrSection.photo}
                    alt="Contact"
                    className="w-full h-full object-cover object-center transform group-hover/photo:scale-105 transition-transform duration-700"
                  />
                </div>

                {/* Additional Info */}
                <div className="space-y-3">
                  <p className="text-foreground leading-relaxed">
                    Save this page for quick access to all my links, resources, and ways to connect.
                    The QR code makes it easy to share with others or bookmark on your phone.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <div className="px-3 py-1.5 bg-orange-500/10 text-orange-600 text-sm rounded-full font-semibold border border-orange-200/50">
                      Always Updated
                    </div>
                    <div className="px-3 py-1.5 bg-amber-500/10 text-amber-600 text-sm rounded-full font-semibold border border-amber-200/50">
                      Easy to Share
                    </div>
                    <div className="px-3 py-1.5 bg-orange-500/10 text-orange-600 text-sm rounded-full font-semibold border border-orange-200/50">
                      Mobile Friendly
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>


      </div>

      <style>{`
        @keyframes float {
          0%, 100% { 
            transform: translate(0px, 0px) scale(1);
          }
          25% { 
            transform: translate(30px, -30px) scale(1.05);
          }
          50% { 
            transform: translate(-20px, 20px) scale(0.95);
          }
          75% { 
            transform: translate(20px, 10px) scale(1.02);
          }
        }

        @keyframes float-delayed {
          0%, 100% { 
            transform: translate(0px, 0px) scale(1) rotate(0deg);
          }
          25% { 
            transform: translate(-25px, 25px) scale(1.03) rotate(2deg);
          }
          50% { 
            transform: translate(30px, -15px) scale(0.97) rotate(-2deg);
          }
          75% { 
            transform: translate(-15px, -20px) scale(1.01) rotate(1deg);
          }
        }

        @keyframes float-slower {
          0%, 100% { 
            transform: translate(0px, 0px) scale(1);
          }
          33% { 
            transform: translate(20px, 30px) scale(1.04);
          }
          66% { 
            transform: translate(-30px, -20px) scale(0.96);
          }
        }

        @keyframes gradient-shift {
          0%, 100% { 
            opacity: 0.4;
            transform: rotate(0deg) scale(1);
          }
          50% { 
            opacity: 0.6;
            transform: rotate(180deg) scale(1.1);
          }
        }

        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .animate-float {
          animation: float 20s ease-in-out infinite;
        }

        .animate-float-delayed {
          animation: float-delayed 25s ease-in-out infinite;
          animation-delay: 2s;
        }

        .animate-float-slower {
          animation: float-slower 30s ease-in-out infinite;
          animation-delay: 5s;
        }

        .animate-gradient-shift {
          animation: gradient-shift 15s ease-in-out infinite;
        }

        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 4s ease infinite;
        }
        
        /* Premium scroll behavior */
        @media (prefers-reduced-motion: no-preference) {
          html {
            scroll-behavior: smooth;
          }
        }

        /* Smooth transitions for all interactive elements */
        * {
          -webkit-tap-highlight-color: transparent;
        }
      `}</style>
    </div>
  );
};

export default Index;
